import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Package, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePaymentsConfig } from "@/components/PayPalCheckout";
import BookingCalendar from "@/components/BookingCalendar";
import BookingConfirmation from "@/components/BookingConfirmation";
import { prettyTime } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "rounded-none border-0 border-b border-pine/25 bg-transparent px-0 py-3 font-sans text-base shadow-none focus-visible:ring-0 focus-visible:border-forest placeholder:text-pine/35";

const toISO = (d) => d.toISOString().slice(0, 10);

const nextWeekdays = (count) => {
  const out = [];
  const d = new Date();
  while (out.length < count) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(new Date(d));
  }
  return out;
};

export default function Booking() {
  const dates = useMemo(() => nextWeekdays(10), []);
  const [selectedDate, setSelectedDate] = useState(toISO(dates[0]));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", notes: "" });
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [tier, setTier] = useState(() => sessionStorage.getItem("bc_selected_tier") || "");
  const payments = usePaymentsConfig();

  useEffect(() => {
    const onTier = (e) => setTier(e.detail);
    window.addEventListener("bc-tier-selected", onTier);
    return () => window.removeEventListener("bc-tier-selected", onTier);
  }, []);

  const clearTier = () => {
    setTier("");
    sessionStorage.removeItem("bc_selected_tier");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    let live = true;
    setLoadingSlots(true);
    setSelectedTime(null);
    axios
      .get(`${API}/bookings/availability`, { params: { date: selectedDate } })
      .then((res) => live && setSlots(res.data.slots))
      .catch(() => live && setSlots([]))
      .finally(() => live && setLoadingSlots(false));
    return () => {
      live = false;
    };
  }, [selectedDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedTime) {
      toast.error("Please pick a time slot first.");
      return;
    }
    setBooking(true);
    try {
      const res = await axios.post(`${API}/bookings`, { ...form, date: selectedDate, time: selectedTime, tier: tier || null, ref_code: localStorage.getItem("bc_ref_code") || null });
      setConfirmed(res.data);
      toast.success("Consultation booked!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Something went wrong. Please try another slot.");
      if (err.response?.status === 409) {
        const res = await axios.get(`${API}/bookings/availability`, { params: { date: selectedDate } });
        setSlots(res.data.slots);
        setSelectedTime(null);
      }
    } finally {
      setBooking(false);
    }
  };

  if (confirmed) {
    return <BookingConfirmation confirmed={confirmed} payments={payments} payer={{ name: form.name, email: form.email }} />;
  }

  return (
    <section id="booking" data-testid="booking-section" className="border-t border-linen">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
        >
          Book a Consultation
        </motion.p>
        <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
            data-testid="booking-heading"
          >
            Pick a time. <span className="italic text-forest">Skip the wait.</span>
          </motion.h2>
          <p className="max-w-sm text-sm leading-relaxed text-pine/60">
            Free 30-minute consultations, Monday to Friday, 9 AM – 5 PM Pacific Time. Your slot is
            reserved instantly.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <BookingCalendar
            dates={dates}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            slots={slots}
            loadingSlots={loadingSlots}
            selectedTime={selectedTime}
            onSelectTime={setSelectedTime}
          />

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="border border-linen bg-white p-8 md:p-10 lg:col-span-5"
            data-testid="booking-form"
          >
            <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">3 — Your details</p>
            {tier && (
              <div className="mb-8 flex items-center justify-between gap-3 border border-forest/30 bg-forest/5 px-4 py-3" data-testid="booking-tier-chip">
                <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-forest">
                  <Package size={14} /> Selected package: {tier}
                </span>
                <button type="button" onClick={clearTier} aria-label="Remove selected package" data-testid="booking-tier-clear" className="text-pine/40 transition-colors hover:text-terra">
                  <X size={14} />
                </button>
              </div>
            )}
            <div className="flex flex-col gap-8">
              <div>
                <Label htmlFor="bk-name" className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Full Name *</Label>
                <Input id="bk-name" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="booking-name-input" className={inputCls} />
              </div>
              <div>
                <Label htmlFor="bk-email" className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Email *</Label>
                <Input id="bk-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="booking-email-input" className={inputCls} />
              </div>
              <div>
                <Label htmlFor="bk-phone" className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Phone</Label>
                <Input id="bk-phone" value={form.phone} onChange={set("phone")} placeholder="(Optional)" data-testid="booking-phone-input" className={inputCls} />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Service of Interest</Label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger data-testid="booking-service-select" className="mt-2 rounded-none border-0 border-b border-pine/25 bg-transparent px-0 shadow-none focus:ring-0">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bookkeeping & Accounting">Bookkeeping &amp; Accounting</SelectItem>
                    <SelectItem value="Tax Preparation & Planning">Tax Preparation &amp; Planning</SelectItem>
                    <SelectItem value="Fractional CFO & Controller">Fractional CFO &amp; Controller</SelectItem>
                    <SelectItem value="Career Coaching">Career Coaching</SelectItem>
                    <SelectItem value="Financial Solution Strategies">Financial Solution Strategies</SelectItem>
                    <SelectItem value="Other">Something else</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="bk-notes" className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Anything we should know?</Label>
                <Textarea id="bk-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="(Optional)" data-testid="booking-notes-input" className={`${inputCls} resize-none`} />
              </div>
            </div>
            <div className="mt-10 border-t border-linen pt-6">
              <p className="mb-6 font-mono text-xs tracking-[0.1em] text-pine/60" data-testid="booking-selection-summary">
                {selectedTime
                  ? `Selected: ${new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} · ${prettyTime(selectedTime)} PT`
                  : "No time slot selected yet"}
              </p>
              <motion.button
                type="submit"
                disabled={booking || !selectedTime}
                whileTap={{ scale: 0.97 }}
                data-testid="booking-submit-button"
                className="w-full border border-forest bg-forest px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-forest disabled:opacity-40"
              >
                {booking ? "Booking..." : "Confirm Booking"}
              </motion.button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
